import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useMatterStore } from './matterStore';

/**
 * List entry from `GET /api/Matter/{pid}/ConfigurableType`.
 */
interface ConfigurableType {
    record_id: string;
    name: string;
    enabled: boolean;
}

/**
 * Single file record within a configurable type. Mirrors `TypeFileResponse`
 * in routers/configurable_type_router.py — all fields are lowercase
 * snake_case as emitted by the Pydantic schema.
 */
export interface TypeFile {
    record_id: string;
    kind: string;
    filename: string;
    language: string;
    qualifier: string;
    text: string;
    content_hash: string;
}

/**
 * Detail response from `GET /api/Matter/{pid}/ConfigurableType/{typeId}`.
 */
interface ConfigurableTypeDetail {
    record_id: string;
    name: string;
    enabled: boolean;
    files: TypeFile[];
}

interface UnpackResult {
    written: number;
    skipped: number;
    removed: number;
    errors: number;
}

/**
 * Pending local change to a type's enabled flag — queued until saveAll().
 */
interface PendingChange {
    enabled: boolean;
}

export type TypeWithPendingState = ConfigurableType & {
    effectiveEnabled: boolean;
    hasPendingChange: boolean;
};

interface SaveResult {
    success: boolean;
    message: string | null;
    unpack_result: UnpackResult | null;
}

interface ActionResult {
    success: boolean;
    message: string | null;
}

/**
 * Built-in type list item from `GET /api/Matter/{pid}/BuiltinType`.
 */
export interface BuiltinType {
    name: string;
}

export const useConfigurableTypeStore = defineStore('configurableType', () => {
    const matterStore = useMatterStore();

    // =========================================================================
    // Configurable type state
    // =========================================================================

    const types = ref<ConfigurableType[]>([]);
    const selectedTypeId = ref<string | null>(null);
    const selectedTypeDetail = ref<ConfigurableTypeDetail | null>(null);
    const loadingList = ref(false);
    const loadingDetail = ref(false);
    const saving = ref(false);

    const pendingChanges = ref<Record<string, PendingChange>>({});

    const hasPendingChanges = computed(() => Object.keys(pendingChanges.value).length > 0);

    const typesWithPendingState = computed<TypeWithPendingState[]>(() => {
        return types.value.map((t) => {
            const pending = pendingChanges.value[t.record_id];
            return {
                ...t,
                effectiveEnabled: pending !== undefined ? pending.enabled : t.enabled,
                hasPendingChange: pending !== undefined,
            };
        });
    });

    const selectedType = computed(
        () => typesWithPendingState.value.find((t) => t.record_id === selectedTypeId.value) ?? null,
    );

    // =========================================================================
    // Built-in type state
    // =========================================================================

    const builtinTypes = ref<BuiltinType[]>([]);
    const selectedBuiltinTypeName = ref<string | null>(null);
    const loadingBuiltinList = ref(false);

    const builtinSmokeTestLoading = ref(false);
    const builtinSmokeTestResults = ref<Record<string, unknown> | null>(null);
    const builtinFilterSmokeTestLoading = ref(false);
    const builtinFilterSmokeTestResults = ref<Record<string, unknown> | null>(null);

    // =========================================================================
    // Helpers
    // =========================================================================

    function matterId() {
        return matterStore.currentMatter?.RecordId;
    }

    function baseUrl() {
        return `/api/Matter/${matterId()}/ConfigurableType`;
    }

    function builtinBaseUrl() {
        return `/api/Matter/${matterId()}/BuiltinType`;
    }

    // =========================================================================
    // Configurable type actions
    // =========================================================================

    async function loadTypes() {
        loadingList.value = true;
        try {
            const res = await fetch(baseUrl());
            if (!res.ok) throw new Error(await res.text());
            types.value = await res.json();
        } finally {
            loadingList.value = false;
        }
    }

    async function selectType(typeId: string | null) {
        // Clear built-in selection when selecting a configurable type
        selectedBuiltinTypeName.value = null;
        clearBuiltinSmokeTestResults();

        selectedTypeId.value = typeId;
        selectedTypeDetail.value = null;
        if (!typeId) return;

        loadingDetail.value = true;
        try {
            const res = await fetch(`${baseUrl()}/${typeId}`);
            if (!res.ok) throw new Error(await res.text());
            selectedTypeDetail.value = await res.json();
        } finally {
            loadingDetail.value = false;
        }
    }

    function clearSelection() {
        selectedTypeId.value = null;
        selectedTypeDetail.value = null;
    }

    function queueEnableChange(typeId: string, enabled: boolean) {
        const dbType = types.value.find((t) => t.record_id === typeId);
        if (dbType && dbType.enabled === enabled) {
            const next = { ...pendingChanges.value };
            delete next[typeId];
            pendingChanges.value = next;
        } else {
            pendingChanges.value = {
                ...pendingChanges.value,
                [typeId]: { enabled },
            };
        }
    }

    async function saveAll(): Promise<SaveResult> {
        saving.value = true;
        try {
            const changes = Object.entries(pendingChanges.value).map(([typeId, change]) => ({
                type_id: typeId,
                enabled: change.enabled,
            }));

            const res = await fetch(`${baseUrl()}/Save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ changes }),
            });

            if (!res.ok) {
                const text = await res.text();
                return { success: false, message: text, unpack_result: null };
            }

            const data = await res.json();

            pendingChanges.value = {};
            types.value = data.types;

            if (selectedTypeId.value) {
                await selectType(selectedTypeId.value);
            }

            return { success: true, message: null, unpack_result: data.unpack_result };
        } catch (err) {
            return {
                success: false,
                message: err instanceof Error ? err.message : String(err),
                unpack_result: null,
            };
        } finally {
            saving.value = false;
        }
    }

    async function importType(name: string, file: File): Promise<ActionResult> {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('file', file);

        const res = await fetch(`${baseUrl()}/Import`, {
            method: 'POST',
            body: formData,
        });

        if (!res.ok) {
            const text = await res.text();
            let message = text;
            try {
                const json = JSON.parse(text);
                message = json.detail ?? text;
            } catch {
                /* plain text */
            }
            return { success: false, message };
        }

        await loadTypes();
        return { success: true, message: null };
    }

    async function deleteType(typeId: string): Promise<ActionResult> {
        const res = await fetch(`${baseUrl()}/${typeId}`, { method: 'DELETE' });
        if (!res.ok) {
            const text = await res.text();
            let message = text;
            try {
                const json = JSON.parse(text);
                message = json.detail ?? text;
            } catch {
                /* plain text */
            }
            return { success: false, message };
        }

        if (selectedTypeId.value === typeId) {
            selectedTypeId.value = null;
            selectedTypeDetail.value = null;
        }

        await loadTypes();
        return { success: true, message: null };
    }

    // Configurable type smoke tests
    const smokeTestLoading = ref(false);
    const smokeTestResults = ref<Record<string, unknown> | null>(null);

    const filterSmokeTestLoading = ref(false);
    const filterSmokeTestResults = ref<Record<string, unknown> | null>(null);

    async function runTypeSmokeTest(typeName: string): Promise<ActionResult> {
        smokeTestLoading.value = true;
        smokeTestResults.value = null;
        try {
            const url = `/api/Matter/${matterId()}/ConfigurableType/${encodeURIComponent(typeName)}/Smoketest/Type`;
            const res = await fetch(url, { method: 'POST' });
            if (!res.ok) {
                const text = await res.text();
                let message = text;
                try {
                    const json = JSON.parse(text);
                    message = json.detail ?? text;
                } catch {
                    /* plain text */
                }
                return { success: false, message };
            }
            smokeTestResults.value = await res.json();
            return { success: true, message: null };
        } catch (err) {
            return { success: false, message: err instanceof Error ? err.message : String(err) };
        } finally {
            smokeTestLoading.value = false;
        }
    }

    async function runFilterSmokeTest(typeName: string): Promise<ActionResult> {
        filterSmokeTestLoading.value = true;
        filterSmokeTestResults.value = null;
        try {
            const url = `/api/Matter/${matterId()}/ConfigurableType/${encodeURIComponent(typeName)}/Smoketest/Filters`;
            const res = await fetch(url, { method: 'POST' });
            if (!res.ok) {
                const text = await res.text();
                let message = text;
                try {
                    const json = JSON.parse(text);
                    message = json.detail ?? text;
                } catch {
                    /* plain text */
                }
                return { success: false, message };
            }
            filterSmokeTestResults.value = await res.json();
            return { success: true, message: null };
        } catch (err) {
            return { success: false, message: err instanceof Error ? err.message : String(err) };
        } finally {
            filterSmokeTestLoading.value = false;
        }
    }

    function clearSmokeTestResults() {
        smokeTestResults.value = null;
        filterSmokeTestResults.value = null;
    }

    // =========================================================================
    // Built-in type actions
    // =========================================================================

    async function loadBuiltinTypes() {
        loadingBuiltinList.value = true;
        try {
            const res = await fetch(builtinBaseUrl());
            if (!res.ok) throw new Error(await res.text());
            builtinTypes.value = await res.json();
        } finally {
            loadingBuiltinList.value = false;
        }
    }

    function selectBuiltinType(typeName: string | null) {
        // Clear configurable selection when selecting a built-in type
        selectedTypeId.value = null;
        selectedTypeDetail.value = null;
        clearSmokeTestResults();

        if (selectedBuiltinTypeName.value === typeName) {
            selectedBuiltinTypeName.value = null;
        } else {
            clearBuiltinSmokeTestResults();
            selectedBuiltinTypeName.value = typeName;
        }
    }

    async function runBuiltinTypeSmokeTest(typeName: string): Promise<ActionResult> {
        builtinSmokeTestLoading.value = true;
        builtinSmokeTestResults.value = null;
        try {
            const url = `${builtinBaseUrl()}/${encodeURIComponent(typeName)}/Smoketest/Type`;
            const res = await fetch(url, { method: 'POST' });
            if (!res.ok) {
                const text = await res.text();
                let message = text;
                try {
                    const json = JSON.parse(text);
                    message = json.detail ?? text;
                } catch {
                    /* plain text */
                }
                return { success: false, message };
            }
            builtinSmokeTestResults.value = await res.json();
            return { success: true, message: null };
        } catch (err) {
            return { success: false, message: err instanceof Error ? err.message : String(err) };
        } finally {
            builtinSmokeTestLoading.value = false;
        }
    }

    async function runBuiltinFilterSmokeTest(typeName: string): Promise<ActionResult> {
        builtinFilterSmokeTestLoading.value = true;
        builtinFilterSmokeTestResults.value = null;
        try {
            const url = `${builtinBaseUrl()}/${encodeURIComponent(typeName)}/Smoketest/Filters`;
            const res = await fetch(url, { method: 'POST' });
            if (!res.ok) {
                const text = await res.text();
                let message = text;
                try {
                    const json = JSON.parse(text);
                    message = json.detail ?? text;
                } catch {
                    /* plain text */
                }
                return { success: false, message };
            }
            builtinFilterSmokeTestResults.value = await res.json();
            return { success: true, message: null };
        } catch (err) {
            return { success: false, message: err instanceof Error ? err.message : String(err) };
        } finally {
            builtinFilterSmokeTestLoading.value = false;
        }
    }

    function clearBuiltinSmokeTestResults() {
        builtinSmokeTestResults.value = null;
        builtinFilterSmokeTestResults.value = null;
    }

    // =========================================================================
    // Reset
    // =========================================================================

    function reset() {
        types.value = [];
        selectedTypeId.value = null;
        selectedTypeDetail.value = null;
        pendingChanges.value = {};
        loadingList.value = false;
        loadingDetail.value = false;
        saving.value = false;
        smokeTestLoading.value = false;
        smokeTestResults.value = null;
        filterSmokeTestLoading.value = false;
        filterSmokeTestResults.value = null;

        builtinTypes.value = [];
        selectedBuiltinTypeName.value = null;
        loadingBuiltinList.value = false;
        builtinSmokeTestLoading.value = false;
        builtinSmokeTestResults.value = null;
        builtinFilterSmokeTestLoading.value = false;
        builtinFilterSmokeTestResults.value = null;
    }

    return {
        // Configurable type state
        types,
        selectedTypeId,
        selectedTypeDetail,
        loadingList,
        loadingDetail,
        saving,
        pendingChanges,
        smokeTestLoading,
        smokeTestResults,
        filterSmokeTestLoading,
        filterSmokeTestResults,
        hasPendingChanges,
        typesWithPendingState,
        selectedType,
        // Configurable type actions
        loadTypes,
        selectType,
        clearSelection,
        queueEnableChange,
        saveAll,
        importType,
        deleteType,
        runTypeSmokeTest,
        runFilterSmokeTest,
        clearSmokeTestResults,
        // Built-in type state
        builtinTypes,
        selectedBuiltinTypeName,
        loadingBuiltinList,
        builtinSmokeTestLoading,
        builtinSmokeTestResults,
        builtinFilterSmokeTestLoading,
        builtinFilterSmokeTestResults,
        // Built-in type actions
        loadBuiltinTypes,
        selectBuiltinType,
        runBuiltinTypeSmokeTest,
        runBuiltinFilterSmokeTest,
        clearBuiltinSmokeTestResults,
        // Reset
        reset,
    };
});
